import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { X, ClockCounterClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { HistoryModal } from "@/pages/ContactsPage";
import { HubActionDialog } from "./HubActionDialog";
import { C, MONO, STAGE_COLORS, STAGE_LABELS, ACTIONS, ago, Pill, Eyebrow } from "./hubShared";

const LIMIT = 50;
const DO_LABELS = { none: "Not requested", requested: "Requested", opened: "Opened form", signed: "Signed" };

export const HubMembers = ({ filters, title, onClear, onChanged }) => {
  const [data, setData] = useState({ total: 0, items: [] });
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [sq, setSq] = useState("");
  const [act, setAct] = useState(null);
  const [hist, setHist] = useState(null);
  const req = useRef(0);

  const load = useCallback(async (s = 0, query = sq) => {
    const id = ++req.current;
    try {
      const { data: d } = await api.get("/comms-hub/members", { params: { ...filters, q: query, skip: s, limit: LIMIT } });
      if (id !== req.current) return;
      setData(d); setSkip(s);
    } catch { if (id === req.current) toast.error("Couldn't load members"); }
  }, [filters, sq]);

  useEffect(() => { load(0); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const openHist = async (m) => {
    try { const r = await api.get(`/contacts/${m.id}/timeline`); setHist(r.data); }
    catch { toast.error("Couldn't load their history"); }
  };

  return (
    <div data-testid="hub-members" style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.10)", borderRadius: 10, padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <Eyebrow>Drill-down</Eyebrow>
          <div style={{ fontSize: 17, fontWeight: 700, color: C.ink, marginTop: 4 }} data-testid="hub-members-title">{title} · <span style={{ fontFamily: MONO }} data-testid="hub-members-total">{data.total}</span></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <form onSubmit={(e) => { e.preventDefault(); setSq(q); load(0, q); }} style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid rgba(26,30,36,0.15)", borderRadius: 8, padding: "0 10px", background: C.body }}>
            <MagnifyingGlass size={14} color={C.slate} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email or cell" data-testid="hub-members-search"
              style={{ border: 0, background: "transparent", padding: "8px 0", fontSize: 12.5, outline: "none", width: 180 }} />
          </form>
          <button onClick={onClear} data-testid="hub-members-close" className="hub-btn" style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.15)", color: C.ink }}><X size={13} /> Close</button>
        </div>
      </div>

      <div style={{ overflowX: "auto", marginTop: 14 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 860 }}>
          <thead>
            <tr style={{ color: C.slate, textAlign: "left" }}>
              {["Member", "Partner", "Stage", "Mailed", "Last mail", "Debit order", ""].map((h) => (
                <th key={h} style={{ padding: "8px 8px", fontWeight: 600, borderBottom: "1px solid rgba(26,30,36,0.08)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.map((m) => (
              <tr key={m.id} data-testid={`hub-member-${m.id}`} className="hub-row" style={{ borderBottom: "1px solid rgba(26,30,36,0.06)" }}>
                <td style={{ padding: "9px 8px" }}>
                  <div style={{ fontWeight: 600, color: C.ink }}>{m.full_name}{m.member_type === "dependant" && <span style={{ fontSize: 10.5, color: C.slate, fontWeight: 500 }}> · dependant</span>}</div>
                  <div style={{ fontSize: 11.5, color: m.bounced ? C.red : C.slate }}>{m.email || "no email"}{m.cell ? ` · ${m.cell}` : ""}</div>
                </td>
                <td style={{ padding: "9px 8px" }}>{m.partner}</td>
                <td style={{ padding: "9px 8px" }}><Pill color={STAGE_COLORS[m.stage]}>{STAGE_LABELS[m.stage]}</Pill></td>
                <td style={{ padding: "9px 8px", fontFamily: MONO }}>{m.times_mailed}×</td>
                <td style={{ padding: "9px 8px" }}>
                  <div>{ago(m.days_since_mail)}</div>
                  {m.last_campaign && <div style={{ fontSize: 11, color: C.slate }}>{m.last_campaign} · {m.last_outcome}</div>}
                </td>
                <td style={{ padding: "9px 8px" }}>{m.member_type === "dependant" ? "—" : DO_LABELS[m.do_status]}</td>
                <td style={{ padding: "9px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                  <button onClick={() => openHist(m)} data-testid={`hub-member-history-${m.id}`} className="hub-btn" style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.15)", color: C.ink, marginRight: 6 }}>
                    <ClockCounterClockwise size={13} /> History
                  </button>
                  {m.next_action && (
                    <button onClick={() => setAct(m)} data-testid={`hub-member-action-${m.id}`} className="hub-btn" style={{ background: C.navy, color: "#fff", border: 0 }}>
                      {ACTIONS[m.next_action].label}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={7} style={{ padding: 20, color: C.slate }}>Nobody here.</td></tr>}
          </tbody>
        </table>
      </div>

      {data.total > LIMIT && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, fontSize: 12, color: C.slate }}>
          <span data-testid="hub-members-range">{skip + 1}–{Math.min(skip + LIMIT, data.total)} of {data.total}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button disabled={skip === 0} onClick={() => load(skip - LIMIT)} data-testid="hub-members-prev" className="hub-btn" style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.15)", color: C.ink }}>Previous</button>
            <button disabled={skip + LIMIT >= data.total} onClick={() => load(skip + LIMIT)} data-testid="hub-members-next" className="hub-btn" style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.15)", color: C.ink }}>Next</button>
          </div>
        </div>
      )}

      <HubActionDialog member={act} onClose={() => setAct(null)} onDone={() => { setAct(null); load(skip); onChanged(); }} />
      {hist && <HistoryModal data={hist} onClose={() => setHist(null)} />}
    </div>
  );
};
